<template>
  <div class="cm-grouped-bar">
    <div
      :id="`grouped-bar-legend-${UUID}`"
      class="cm-grouped-bar__legend"
    />
    <svg
      :id="`grouped-bar-${UUID}`"
      width="640"
      height="300"
    />
    <div
      :id="`grouped-bar-tooltip-${UUID}`"
      class="cm-grouped-bar__tooltip"
    />
  </div>
</template>

<script>
import * as d3 from "d3";
import { createUUID } from "@/utils/general";

export default {
  name: "CmGroupedBar",

  props: {
    cid: {
    },
    mainKey: {
      type: String,
      required: true,
    },
    value: {
      type: Array,
      // required: true
    },
    labels: {
      type: Object,
      required: true,
    },
    colors: {
      type: Array,
      default: () => ["#00CA69", "#56D9FE", "#FF8373"],
    },
    paddingInner: {
      type: Number,
      default: 0.65,
    },
    isHourly: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      UUID: createUUID(),
      timeFormat: d3.utcFormat("%x"),
    };
  },

  computed: {
    xKeys() {
      return this.isHourly ?
        [
          "00", "01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11",
          "12", "13", "14", "15", "16", "17", "18", "19", "20", "21", "22", "23",
        ] :
        ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    },
    dateFormat() {
      return this.isHourly ? d3.utcFormat("%H") : d3.utcFormat("%a");
    },
  },

  watch: {
    value(next) {
      this.generateBar(next);
    },
  },

  beforeMount() {
    if (this.cid) this.UUID = this.cid;
  },

  mounted() {
    this.setD3TimeFormat();
    this.generateBar(this.value);
  },

  methods: {
    setD3TimeFormat() {
      const timeLocale = {
        "dateTime": "%A, %e %B %Y г. %X",
        "date": "%d.%m.%Y",
        "time": "%H:%M:%S",
        "periods": ["AM", "PM"],
        "days": ["воскресенье", "понедельник", "вторник", "среда", "четверг", "пятница", "суббота"],
        "shortDays": ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
        "months": ["января", "февраля", "марта", "апреля", "мая", "июня", "июля", "августа", "сентября", "октября", "ноября", "декабря"],
        "shortMonths": ["янв", "фев", "мар", "апр", "май", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"],
      };

      d3.timeFormatDefaultLocale(timeLocale);
      this.timeFormat = d3.utcFormat("%x");
    },
    generateBar(rawData) {
      const timeZoneOffset = new Date().getTimezoneOffset();
      rawData.forEach((item, index) => {
        rawData[index].date = Number(item.date) - timeZoneOffset * 60;
      });

      const that = this;
      let data = _.cloneDeep(rawData);
      d3.selectAll(`#grouped-bar-${this.UUID} *`).remove();

      const svg = d3.select(`#grouped-bar-${this.UUID}`);
      const margin = { top: 20, right: 0, bottom: 30, left: 45 };
      const yTickFormat = d3.format("~s");
      const height = parseInt(svg.style("height")) - margin.bottom - 5,
        width = parseInt(svg.style("width")) - margin.right,
        axisTicks = { qty: 6, outerSize: 0 },
        mainKey = this.mainKey,
        keys = Object.keys(this.labels);

      data = this.formData(data, keys);

      const x0 = d3.scaleBand()
        .domain(this.xKeys)
        .rangeRound([margin.left, width - margin.right])
        .paddingInner(this.paddingInner); // adjusting width of one bar

      const x1 = d3.scaleBand()
        .domain(keys)
        .rangeRound([0, x0.bandwidth()])
        .padding(0);

      function extraBigExcluder(arr) { // exclude too high value from calculations of scale
        const maxValue = d3.max(arr, obj => d3.max(keys, key => obj[key]));
        let meanArr = [];
        arr.forEach((object) => {
          let localMax = 0.1;
          let allObjectValues = []; // add extra parameter to check extra big values between keys in every object
          for (const key in object) {
            if (typeof object[key] === 'number') {
              if (object[key] > localMax) localMax = object[key];
              allObjectValues.push(object[key]);
            }
          }
          allObjectValues = allObjectValues.sort((a, b) => a - b).filter(val => val !== localMax); // need to leave at least 1 highest value except localMax
          const extraBigValueFactor = allObjectValues[0] ? localMax / allObjectValues[0] : false; // check if value exist and calc factor or false
          if (localMax !== maxValue && extraBigValueFactor < 10 && extraBigValueFactor !== false) meanArr.push(object); // if factor is too high - not include this object to meanArr
        });
        const meanValue = d3.mean(meanArr, obj => d3.max(keys, key => obj[key]));
        const factor = maxValue / meanValue;
        if (meanValue && factor > 15) return extraBigExcluder(meanArr);
        return meanArr.length ? meanArr : arr;
      }
      let scaleYData = extraBigExcluder(data);

      const y = d3.scaleLinear()
        .domain([0, d3.max(scaleYData, d => d3.max(keys, key => d[key])) * 1.5]).nice()
        .rangeRound([height, margin.top]);

      const color = d3.scaleOrdinal()
        .range(this.colors);

      const xAxis = g => g
        .attr("transform", `translate(0,${height + 5})`)
        .call(d3.axisBottom(x0))
        .call(g => g.select(".domain").remove())
        .call(g => g.selectAll(".tick line").style("opacity", 0));

      const yAxis = g => {
        g.attr("transform", `translate(${margin.left}, 0)`)
          .call(d3.axisRight(y).ticks(axisTicks.qty)
            .tickSize(width - margin.left - margin.right)
            .tickFormat(d => yTickFormat(d)))
          .call(g => g.select(".domain").remove())
          .call(g => g.selectAll(".tick line")
            .attr("stroke", "#EAF0F4"))
          .call(g => g.selectAll(".tick text")
            .attr("x", -35)
            .attr("dy", 2));

        if (g.selectAll(".tick line").size() < 2) {
          g.attr("transform", `translate(${margin.left}, 110)`);
        }
      };

      svg.append("g")
        .call(xAxis);

      svg.append("g")
        .call(yAxis);

      const barRadius = 5;

      svg.append("g")
        .selectAll("g")
        .data(data)
        .join("g")
        .attr("transform", d => `translate(${x0(d.date)},0)`)
        .selectAll("bar")
        .data((d, index) => keys.map(key => ({
          index, key,
          value: d[key],
          dates: d.dates,
        })))
        .enter()
        .append("path")
        .style("fill", d => color(d.key))
        .attr("d", item => {
          const r = height - y(item.value) - barRadius >= 0 ? barRadius : height - y(item.value) + 1;
          if (item.value === 0) return "";
          return `
            M${x1(item.key)},${y(item.value) + r}
            a${r},${r} 0 0 1 ${r},${-r}
            h${x1.bandwidth() - 2 * r}
            a${r},${r} 0 0 1 ${r},${r}
            v${height - y(item.value) - r}
            h${-(x1.bandwidth())}Z
          `;
        })
        .on("mousemove", function(e, d) {
          that.showTooltip(d, [e.clientX, e.clientY]);

          d3.select(this)
            .style("opacity", 0.8);
        })
        .on("mouseleave", function() {
          d3.select(`#grouped-bar-tooltip-${that.UUID}`)
            .style("display", "none");

          d3.select(this)
            .style("opacity", 1);
        });

      this.createLegend(color, width);
    },

    showTooltip(info, coords) {
      const tooltipHtml = this.isHourly ? this.getHourlyTooltip(info) : this.getDatesTooltip(info);
      d3.select(`#grouped-bar-tooltip-${this.UUID}`).html(tooltipHtml)
        .style("left", coords[0] + 17 + "px")
        .style("top", coords[1] + 10 + "px")
        .style("display", "block");
    },

    formData(data, keys) {
      // this.xKeys = this.xKeys.map(i => `${i}:00`);

      const formedData = data.map(i => {
        const isZeroData = !(keys.reduce((total, k) => total + i[k], 0));
        return {
          ...i,
          originalDate: isZeroData ? 0 : i.date,
          date: this.dateFormat(mt.tsToDateObj(i.date)),
          // date: `${this.dateFormat(mt.tsToDateObj(i.date))}:00`
        };
      });

      let groupedData = d3.groups(formedData, d => d.date);

      groupedData = groupedData.map(i => {
        const res = {
          date: i[0],
          dates: i[1].filter(d => d.originalDate).map(d => d.originalDate),
        };

        keys.forEach(k => res[k] = d3.sum(i[1], d => d[k]));
        return res;
      });

      this.$emit("get-formed-data", groupedData);

      return groupedData;
    },

    createLegend(color, width) {
      const container = d3.select(`#grouped-bar-legend-${this.UUID}`);
      const item = container.selectAll("div")
        .data(color.domain())
        .enter()
        .append("div")
        .attr("class", "legend-item");

      item.append("span")
        .attr("class", "legend-color")
        .style("background-color", color);

      item.append("span")
        .text(d => this.labels[d]);
    },

    getDatesTooltip(info) {
      const uniqueDates = [...new Set(info.dates.map(
        d => this.timeFormat(mt.tsToDateObj(d)),
      ))];

      return `<div class="tooltip-container">
        <h4>${this.labels[info.key]}</h4>
        <span>${this.fNum.format(info.value)}&nbsp;шт.</span>
        <div class="tooltip-dates">${uniqueDates.reduce((str, d, index) => {
    return `${str + d}</span>${index < info.dates.length - 1 ? '<span>' : ''}`;
  }, "<span>")}
        </div>
      </div>`;
    },

    getHourlyTooltip(info) {
      const hourFormat = d3.utcFormat("%H");

      const uniqueHours = [...new Set(info.dates.map(
        d => hourFormat(mt.tsToDateObj(d)),
      ))];

      return `<div class="tooltip-container">
        <h4>${this.labels[info.key]}</h4>
        <span>${this.fNum.format(info.value)}&nbsp;шт.</span>
        <div class="tooltip-dates">${uniqueHours.reduce((str, d, index) => {
    return `${str + d}:00</span>${index < info.dates.length - 1 ? '<span>' : ''}`;
  }, "<span>")}
        </div>
      </div>`;
    },
  },
};
</script>

<style lang="scss">
.cm-grouped-bar {
  position: relative;
  height: 100%;
  &__tooltip {
    background: $color_white;
    box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
    border-radius: 4px;
    position: fixed;
    display: none;
    z-index: 999;
    .tooltip-container {
      display: flex;
      flex-direction: column;
      font-size: 12px;
      padding: 15px 10px;
      h4 {
        font-weight: 500;
        margin: 0 0 5px;
      }
      span {
        font-weight: 300;
      }
      .tooltip-dates {
        display: flex;
        flex-direction: column;
        overflow-y: hidden;
        max-height: 100px;
      }
    }
  }
  &__legend {
    display: flex;
    justify-content: center;
    .legend-item {
      display: flex;
      align-items: center;
      &:not(:first-child) {
        margin-left: 54px;
      }
    }
    .legend-color {
      border-radius: 5px;
      width: 23px;
      height: 12px;
      padding-top: 1px;
      margin-right: 14px;
    }
  }
  .tick {
    text {
      font-size: 11px;
    }
  }

}
</style>
